import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, CheckCircle2, AlertTriangle, SkipForward, KeyRound } from "lucide-react";

export function ImportResultDialog({ entity, result, onClose, onDownloadErrors }) {
  const skipped = result?.skipped || [];
  const errors = result?.errors || [];
  const generated = result?.generated_passwords || [];
  const failed = result?.failed_rows || [];
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`import-result-${entity}`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Import Complete
          </DialogTitle>
          <DialogDescription>
            {result?.created || 0} created · {result?.updated || 0} updated · {skipped.length} skipped · {errors.length} error(s) — from {result?.total_rows || 0} row(s).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {generated.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3" data-testid="import-generated-passwords">
              <p className="text-xs font-semibold text-blue-700 flex items-center gap-1 mb-2">
                <KeyRound className="h-3.5 w-3.5" /> Temporary passwords (share securely — shown once)
              </p>
              <table className="w-full text-xs">
                <tbody className="divide-y divide-blue-100">
                  {generated.map((g) => (
                    <tr key={g.username}>
                      <td className="py-1 font-mono-data text-slate-700">@{g.username}</td>
                      <td className="py-1 font-mono-data text-blue-700 text-right">{g.password}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {skipped.length > 0 && (
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1.5">
                <SkipForward className="h-3.5 w-3.5 text-amber-500" /> Skipped ({skipped.length})
              </p>
              <ul className="text-[11px] text-slate-500 space-y-0.5">
                {skipped.slice(0, 50).map((s, i) => (
                  <li key={`skip-${s.row}-${i}`}>Row {s.row}: <b className="text-slate-700">{s.key}</b> — {s.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {errors.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
              <p className="text-xs font-semibold text-red-600 flex items-center gap-1 mb-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Errors ({errors.length})
              </p>
              <ul className="text-[11px] text-red-700 space-y-0.5">
                {errors.slice(0, 50).map((er, i) => (
                  <li key={`err-${er.row}-${i}`}>Row {er.row}: {er.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {result?.created > 0 && skipped.length === 0 && errors.length === 0 && (
            <p className="text-sm text-emerald-600 text-center py-2">All rows imported cleanly. 🎉</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {failed.length > 0 ? (
            <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={onDownloadErrors} data-testid={`download-errors-${entity}`}>
              <Download className="h-4 w-4 mr-1.5" /> Download error report ({failed.length})
            </Button>
          ) : <span />}
          <Button onClick={onClose} data-testid={`import-result-close-${entity}`}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
